import { CSSProperties, useRef, useState } from "react";
import { BsFunnel, BsFunnelFill } from "react-icons/bs";
import useOutsideClick from "../../../hooks/useOutsideClick";
import "./FilterPopup.css";

export type filterPopupItem = {
    key: string;
    label: string;
    selected?: boolean;
};

export type filterPopupGroup = {
    key: string;
    label: string;
    items: filterPopupItem[];
};

export type filterPopupProps = {
    title: string; // tooltip on the icon, e.g. the active filters
    groups: filterPopupGroup[];
    onApply: (selectedKeys: string[]) => void; // called on Ok with one item key per filtered group, empty to clear
    styles?: CSSProperties;
};

type groupSelection = Record<string, string | undefined>; // group key -> selected item key

// Funnel icon that pops a panel with a labelled dropdown per filter group. Each group can be set independently.
// Changes are held as pending until Ok is clicked; Cancel or clicking outside discards them.
const FilterPopup = (props: filterPopupProps) => {
    const popupRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [pending, setPending] = useState<groupSelection>({});

    const selected: groupSelection = Object.fromEntries(
        props.groups.map(g => [g.key, g.items.find(x => x.selected)?.key]),
    );
    const selectedKeys = Object.values(selected).filter((x): x is string => !!x);
    const pendingKeys = Object.values(pending).filter((x): x is string => !!x);

    useOutsideClick(popupRef, () => setOpen(false));

    const toggleOpen = () => {
        if (!open) setPending(selected);
        setOpen(!open);
    };

    const apply = () => {
        setOpen(false);
        const unchanged =
            pendingKeys.length === selectedKeys.length && pendingKeys.every(key => selectedKeys.includes(key));
        if (!unchanged) props.onApply(pendingKeys);
    };

    return (
        <div
            className="g-filter-popup"
            ref={popupRef}
            style={props.styles}
        >
            <div
                className={
                    (open ? "icon-button-selected" : "icon-button") +
                    " g-filter-popup-button" +
                    (selectedKeys.length > 0 ? " active" : "")
                }
                title={props.title}
                onClick={toggleOpen}
            >
                {selectedKeys.length > 0 ? (
                    <BsFunnelFill style={{ width: "14px", height: "14px" }} />
                ) : (
                    <BsFunnel style={{ width: "14px", height: "14px" }} />
                )}
            </div>
            {open && (
                <div className="g-filter-popup-panel">
                    <div className="g-filter-popup-header">
                        <span>Filter enquiries</span>
                        {pendingKeys.length > 0 && (
                            <button
                                className="g-filter-popup-clear"
                                onClick={() => setPending({})}
                            >
                                Clear all
                            </button>
                        )}
                    </div>
                    {props.groups.map(group => {
                        const pendingKey = pending[group.key];
                        const selectId = "g-filter-popup-" + group.key.replace(/\s/g, "");
                        return (
                            <div
                                key={group.key}
                                className="g-filter-popup-row"
                            >
                                <label htmlFor={selectId}>{group.label}</label>
                                <select
                                    id={selectId}
                                    className={"form-select form-select-sm" + (pendingKey ? " selected" : "")}
                                    value={pendingKey ?? ""}
                                    onChange={e => setPending({ ...pending, [group.key]: e.target.value || undefined })}
                                >
                                    <option value="">All</option>
                                    {group.items.map(item => (
                                        <option
                                            key={item.key}
                                            value={item.key}
                                        >
                                            {item.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        );
                    })}
                    <div className="g-filter-popup-buttons">
                        <button
                            className="g-btn g-btn-action"
                            onClick={apply}
                        >
                            Ok
                        </button>
                        <button
                            className="g-btn g-btn-alternate"
                            onClick={() => setOpen(false)}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FilterPopup;
